import { Money } from '../dinheiro/money.js'
import type { Moeda } from '../dinheiro/moeda.js'

/**
 * Os 17 grupos da DRE (DS-545).
 *
 * A lista mora aqui, uma vez só. Antes ela existia como um switch copiado no leitor da DRE
 * e na ponte da rentabilidade, e o grupo que o switch não reconhecia sumia da DRE sem
 * aviso. O CHECK conta_contabil_grupo_dre_valido (0148), o contrato e a tela da 8.4 a
 * espelham.
 */
export const GRUPOS_DA_DRE = [
  'receita_propria', 'receita_repasse', 'deducao', 'repasse', 'comissao',
  'custos_pj', 'custos_tecnologia', 'pessoal', 'despesas_pj',
  'administrativas', 'tecnologia', 'marketing', 'viagens',
  'investimento', 'amortizacao', 'resultado_financeiro', 'tributos_resultado',
] as const

export type GrupoDaDre = (typeof GRUPOS_DA_DRE)[number]

const CONJUNTO_DOS_GRUPOS: ReadonlySet<string> = new Set(GRUPOS_DA_DRE)

export function ehGrupoDaDre(valor: string | null): valor is GrupoDaDre {
  return valor !== null && CONJUNTO_DOS_GRUPOS.has(valor)
}

const GRUPOS_DE_RECEITA: readonly GrupoDaDre[] = ['receita_propria', 'receita_repasse']
const GRUPOS_FINANCEIROS: readonly GrupoDaDre[] = ['resultado_financeiro']
/*
  Os 12 grupos de custo e despesa. Ficam de fora os de receita, o financeiro e os dois que
  o próprio sistema preenche: 'investimento' vem da NATUREZA da conta e 'amortizacao' da
  agenda do intangível (v_dre_lancamento, 0117:85 e :99).
*/
const GRUPOS_DE_DESPESA: readonly GrupoDaDre[] = GRUPOS_DA_DRE.filter((grupo) =>
  !GRUPOS_DE_RECEITA.includes(grupo) && !GRUPOS_FINANCEIROS.includes(grupo)
  && grupo !== 'investimento' && grupo !== 'amortizacao')

/**
 * Os grupos que uma conta aceita, pela natureza e pelo tipo (DS-545). É a regra do CHECK
 * conta_contabil_grupo_dre_valido: sintética, movimentação e investimento ficam sem grupo;
 * a analítica de receita, de despesa ou financeira tem de ter um da lista dela. A
 * 'deducao' está aqui porque as 2.1 a 2.5 a usam; quem não a oferece é a tela.
 */
export function gruposDaDreDaConta(
  natureza: 'receita' | 'despesa' | 'movimentacao' | 'investimento' | 'financeira',
  tipo: 'Sintética' | 'Analítica',
): readonly GrupoDaDre[] {
  if (tipo === 'Sintética') return []
  switch (natureza) {
    case 'receita': return GRUPOS_DE_RECEITA
    case 'despesa': return GRUPOS_DE_DESPESA
    case 'financeira': return GRUPOS_FINANCEIROS
    case 'movimentacao': case 'investimento': return []
  }
}

/** Um fato da DRE que chegou sem um dos 17 grupos (DS-545). */
export class FatoDaDreSemGrupoValido extends Error {
  constructor(readonly contaCodigo: string | undefined, readonly grupo: string | null) {
    super(`Fato da DRE sem Grupo na DRE válido (conta ${contaCodigo ?? 'sem código'}, grupo ${grupo ?? 'vazio'}).`)
    this.name = 'FatoDaDreSemGrupoValido'
  }
}

export interface FatoParaSomarNaDre {
  readonly grupo: string | null
  readonly valor: string
  readonly contaCodigo?: string | undefined
}

/**
 * Soma os fatos por grupo da DRE, e FALHA ALTO no grupo desconhecido (EI-17, DS-545).
 *
 * O leitor antigo pulava o fato com grupo fora da lista, e o resultado melhorava sem
 * ninguém ver. Com o CHECK da 0148 esse fato é impossível; se ainda assim aparecer, o
 * certo é o erro, nunca o número errado. `nuloPula` existe por causa do Orçado: a linha
 * "Sem conta" do orçamento não tem conta nem grupo por desenho. Os fatos realizados
 * somam com `nuloPula: false`.
 */
export function somarFatosDaDre(
  fatos: readonly FatoParaSomarNaDre[],
  opcoes: { readonly moeda: Moeda; readonly nuloPula: boolean },
): Partial<Record<GrupoDaDre, Money>> {
  const grupos: Partial<Record<GrupoDaDre, Money>> = {}
  for (const fato of fatos) {
    if (fato.grupo === null && opcoes.nuloPula) continue
    if (!ehGrupoDaDre(fato.grupo)) throw new FatoDaDreSemGrupoValido(fato.contaCodigo, fato.grupo)
    const grupo = fato.grupo
    grupos[grupo] = (grupos[grupo] ?? Money.zero(opcoes.moeda)).somar(Money.de(fato.valor, opcoes.moeda))
  }
  return grupos
}

/**
 * As 23 linhas da DRE gerencial, na ordem da apresentação (D-169, DS-603).
 *
 * Até a v1.3.5 a cascata tinha 17 linhas (AGI e Margem de contribuição no meio, e o Investimento
 * PageSignal subtraído junto com a amortização). A estrutura aprovada separa Custos Diretos de
 * Despesas Operacionais por grupo, chama de Lucro Bruto o que sobra depois dos custos diretos, e tira
 * o investimento da DRE: ele só chega pela Depreciação e Amortização. AGI e Margem de contribuição
 * continuam calculadas, como indicadores auxiliares (`indicadoresAuxiliaresDaDre`).
 */
export type CodigoDaCascata =
  | 'receita_propria' | 'receita_repasse' | 'receita_bruta' | 'deducoes' | 'receita_liquida'
  | 'custos_midia' | 'custos_comissoes' | 'custos_servicos_pj' | 'custos_tecnologia' | 'lucro_bruto'
  | 'despesas_pessoal' | 'despesas_servicos_pj' | 'despesas_administrativas' | 'despesas_tecnologia'
  | 'despesas_marketing' | 'despesas_viagens' | 'ebitda'
  | 'depreciacao_amortizacao' | 'resultado_operacional' | 'resultado_financeiro'
  | 'resultado_antes_tributos' | 'irpj_csll' | 'resultado_liquido'

export interface LinhaDaCascata {
  readonly codigo: CodigoDaCascata
  readonly valor: Money
}

/**
 * Os grupos da DRE que formam cada linha (DS-603). É daqui que a apresentação deriva a conta de cada
 * linha no banco (a conta do grupo, a sintética que agrupa exatamente aquelas contas, ou um filtro por
 * grupo), em vez de um código fixo. Resultado e subtotal de linhas diferentes não têm entrada, exceto a
 * Receita Bruta, que é exatamente as duas receitas. 'amortizacao' não é grupo de conta: é a agenda do
 * ativo intangível, e a conta dela é a da chave `amortizacao_intangivel`.
 */
export const GRUPOS_DA_LINHA: Readonly<Partial<Record<CodigoDaCascata, readonly GrupoDaDre[]>>> = {
  receita_propria: ['receita_propria'],
  receita_repasse: ['receita_repasse'],
  receita_bruta: ['receita_propria', 'receita_repasse'],
  deducoes: ['deducao'],
  custos_midia: ['repasse'],
  custos_comissoes: ['comissao'],
  custos_servicos_pj: ['custos_pj'],
  custos_tecnologia: ['custos_tecnologia'],
  despesas_pessoal: ['pessoal'],
  despesas_servicos_pj: ['despesas_pj'],
  despesas_administrativas: ['administrativas'],
  despesas_tecnologia: ['tecnologia'],
  despesas_marketing: ['marketing'],
  despesas_viagens: ['viagens'],
  depreciacao_amortizacao: ['amortizacao'],
  resultado_financeiro: ['resultado_financeiro'],
  irpj_csll: ['tributos_resultado'],
}

function somar(grupos: Readonly<Partial<Record<GrupoDaDre, Money>>>, moeda: Moeda,
  ...chaves: readonly GrupoDaDre[]): Money {
  return chaves.reduce((total, chave) => total.somar(grupos[chave] ?? Money.zero(moeda)), Money.zero(moeda))
}

/**
 * A cascata gerencial em um único cálculo puro (D-169). Entradas analíticas são magnitudes: receita
 * soma; deduções, custos e despesas são subtraídos aqui. O resultado financeiro é a magnitude líquida
 * da despesa financeira (juros e tarifas − rendimentos), negativa quando os rendimentos superam. O
 * grupo 'investimento' (a natureza da conta) não entra em linha nenhuma: investimento chega à DRE só
 * pela amortização.
 */
export function cascataDaDre(
  grupos: Readonly<Partial<Record<GrupoDaDre, Money>>>,
  moeda: Moeda,
): readonly LinhaDaCascata[] {
  const receitaPropria = somar(grupos,moeda,'receita_propria')
  const receitaRepasse = somar(grupos,moeda,'receita_repasse')
  const receitaBruta = receitaPropria.somar(receitaRepasse)
  const deducoes = somar(grupos,moeda,'deducao')
  const receitaLiquida = receitaBruta.subtrair(deducoes)
  const custosMidia = somar(grupos,moeda,'repasse')
  const custosComissoes = somar(grupos,moeda,'comissao')
  const custosServicosPj = somar(grupos,moeda,'custos_pj')
  const custosTecnologia = somar(grupos,moeda,'custos_tecnologia')
  const lucroBruto = receitaLiquida.subtrair(custosMidia).subtrair(custosComissoes)
    .subtrair(custosServicosPj).subtrair(custosTecnologia)
  const despesasPessoal = somar(grupos,moeda,'pessoal')
  const despesasServicosPj = somar(grupos,moeda,'despesas_pj')
  const despesasAdministrativas = somar(grupos,moeda,'administrativas')
  const despesasTecnologia = somar(grupos,moeda,'tecnologia')
  const despesasMarketing = somar(grupos,moeda,'marketing')
  const despesasViagens = somar(grupos,moeda,'viagens')
  const ebitda = lucroBruto.subtrair(despesasPessoal).subtrair(despesasServicosPj)
    .subtrair(despesasAdministrativas).subtrair(despesasTecnologia).subtrair(despesasMarketing)
    .subtrair(despesasViagens)
  const depreciacaoAmortizacao = somar(grupos,moeda,'amortizacao')
  const resultadoOperacional = ebitda.subtrair(depreciacaoAmortizacao)
  const resultadoFinanceiro = somar(grupos,moeda,'resultado_financeiro')
  const resultadoAntesTributos = resultadoOperacional.subtrair(resultadoFinanceiro)
  const irpjCsll = somar(grupos,moeda,'tributos_resultado')
  const resultadoLiquido = resultadoAntesTributos.subtrair(irpjCsll)

  return [
    { codigo:'receita_propria',valor:receitaPropria },
    { codigo:'receita_repasse',valor:receitaRepasse },
    { codigo:'receita_bruta',valor:receitaBruta },
    { codigo:'deducoes',valor:deducoes },
    { codigo:'receita_liquida',valor:receitaLiquida },
    { codigo:'custos_midia',valor:custosMidia },
    { codigo:'custos_comissoes',valor:custosComissoes },
    { codigo:'custos_servicos_pj',valor:custosServicosPj },
    { codigo:'custos_tecnologia',valor:custosTecnologia },
    { codigo:'lucro_bruto',valor:lucroBruto },
    { codigo:'despesas_pessoal',valor:despesasPessoal },
    { codigo:'despesas_servicos_pj',valor:despesasServicosPj },
    { codigo:'despesas_administrativas',valor:despesasAdministrativas },
    { codigo:'despesas_tecnologia',valor:despesasTecnologia },
    { codigo:'despesas_marketing',valor:despesasMarketing },
    { codigo:'despesas_viagens',valor:despesasViagens },
    { codigo:'ebitda',valor:ebitda },
    { codigo:'depreciacao_amortizacao',valor:depreciacaoAmortizacao },
    { codigo:'resultado_operacional',valor:resultadoOperacional },
    { codigo:'resultado_financeiro',valor:resultadoFinanceiro },
    { codigo:'resultado_antes_tributos',valor:resultadoAntesTributos },
    { codigo:'irpj_csll',valor:irpjCsll },
    { codigo:'resultado_liquido',valor:resultadoLiquido },
  ]
}

/** Os indicadores que saíram do corpo da cascata e continuam calculados (D-169, DS-603). */
export interface IndicadoresAuxiliaresDaDre {
  /** AGI: receita líquida menos o custo de mídia (repasse). O mesmo cálculo da v1.3.5. */
  readonly agi: Money
  /**
   * Margem de contribuição: AGI menos comissões e os custos diretos de PJ e de tecnologia. O mesmo
   * cálculo da v1.3.5; nesta estrutura ele coincide com o Lucro Bruto, porque os custos diretos são
   * exatamente esses grupos. A margem por comportamento variável/fixo é a da Variância (8.3), que lê o
   * atributo `variavel` da linha do orçamento.
   */
  readonly margemContribuicao: Money
}

export function indicadoresAuxiliaresDaDre(
  grupos: Readonly<Partial<Record<GrupoDaDre, Money>>>,
  moeda: Moeda,
): IndicadoresAuxiliaresDaDre {
  const receitaLiquida = somar(grupos,moeda,'receita_propria','receita_repasse')
    .subtrair(somar(grupos,moeda,'deducao'))
  const agi = receitaLiquida.subtrair(somar(grupos,moeda,'repasse'))
  const margemContribuicao = agi.subtrair(somar(grupos,moeda,'comissao'))
    .subtrair(somar(grupos,moeda,'custos_pj','custos_tecnologia'))
  return { agi, margemContribuicao }
}
